import { TaskState } from "@coworker/apprestructured/src/tasks/enums/taskState";
import {
  Question,
  QuestionTaskType,
  VotesByArea,
} from "../BackendApiRepository/QuestionResponse";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";

export interface MfaqV2TaskDetails {
  question_id?: string;
  question_area?: string;
  description?: string;
  upvotes?: number;
}

// Sorts tasks by created_at in descending order (newest first). QuestionTaskType.created_at is typed as a number,
// but the actual type returned by the service is a string (ISO date). Just in case this would change in the future
// make it more resilient by handling both types.
function toTimestamp(value: number): number {
  return typeof value === "string" ? new Date(value).getTime() : value;
}
function sortByCreatedAtDesc(a: QuestionTaskType, b: QuestionTaskType) {
  return toTimestamp(b.created_at) - toTimestamp(a.created_at);
}

export function getActiveTask(question: Question, area: string) {
  const activeTask = [...(question?.tasks ?? [])]
    .sort(sortByCreatedAtDesc)
    .find(
      (task: QuestionTaskType) =>
        task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.CLOSED &&
        task.area === area
    );
  return activeTask
    ? { hasOngoingTask: true, taskId: activeTask.task_id }
    : { hasOngoingTask: false, taskId: null };
}
export function createTaskHistory(question: Question, area: string) {
  const completedTasks = [...(question?.tasks ?? [])]
    .sort(sortByCreatedAtDesc)
    .filter(function (task: QuestionTaskType) {
      return task.state === TaskState.COMPLETED && task.area === area;
    });
  return completedTasks;
}

export function toQuestionTaskType(task: Task): QuestionTaskType {
  const details = task.details as MfaqV2TaskDetails;
  return {
    area: details.question_area ?? "",
    finished_summary: details.description ?? "",
    finished_at:
      task.state === "completed" ? new Date(task.completed_at).getTime() : 0,
    finished_votes_count: details.upvotes ?? 0,
    created_at: new Date(task.created_at).getTime(),
    description: details.description ?? "",
    task_id: task.id,
    state: task.state,
  };
}

export function calculateNumberOfVotes(question: Question, area: string) {
  if (area === "whole=whole") {
    return question?.upvotes_by_area?.reduce(
      (total: number, item: VotesByArea) => total + item.votes_unresolved,
      0
    );
  } else {
    const foundArea = question?.upvotes_by_area?.find(
      (item: { area: string }) => item.area === area
    );
    return foundArea ? foundArea?.votes_unresolved : 0;
  }
}

export function onUpvote(
  questionId: string,
  area: string,
  storeId: string,
  createVote: (
    questionId: string,
    value: number,
    voteLocation: string,
    storeId: string
  ) => Promise<unknown>,
  setQuestion: React.Dispatch<React.SetStateAction<Question | undefined>>,
  question: Question
) {
  handleVote(questionId, area, storeId, createVote, setQuestion, question, 1);
}

export function onDownvote(
  questionId: string,
  area: string,
  storeId: string,
  createVote: (
    questionId: string,
    value: number,
    voteLocation: string,
    storeId: string
  ) => Promise<unknown>,
  setQuestion: React.Dispatch<React.SetStateAction<Question | undefined>>,
  question: Question
) {
  handleVote(questionId, area, storeId, createVote, setQuestion, question, -1);
}

function handleVote(
  questionId: string,
  area: string,
  storeId: string,
  createVote: (
    questionId: string,
    value: number,
    voteLocation: string,
    storeId: string
  ) => Promise<unknown>,
  setQuestion: React.Dispatch<React.SetStateAction<Question | undefined>>,
  question: Question,
  voteType: 1 | -1
) {
  createAreaIfMissing(question, area);
  const updatedUpvotesByArea = question.upvotes_by_area.map(
    (upvoteArea: VotesByArea) => {
      if (upvoteArea.area === area) {
        return {
          ...upvoteArea,
          votes_unresolved: upvoteArea.votes_unresolved + voteType,
        };
      } else return upvoteArea;
    }
  );
  setQuestion({
    ...question,
    upvotes_by_area: updatedUpvotesByArea,
  });
  createVote(questionId, voteType, area, storeId);
}

function createAreaIfMissing(question: Question, area: string) {
  const foundArea = question.upvotes_by_area.find(
    (item: { area: string }) => item.area === area
  );
  if (!foundArea) {
    const newArea: VotesByArea = {
      area: area,
      votes_unresolved: 0,
      votes_resolved: 0,
      votes: 0,
    };
    question.upvotes_by_area.push(newArea);
  }
}

export function sortUpvotesByArea(upVotesByArea: VotesByArea[]) {
  const sorted = upVotesByArea?.sort(
    (a: VotesByArea, b: VotesByArea) => b.votes_unresolved - a.votes_unresolved
  );
  return sorted?.filter((area: VotesByArea) => area.votes_unresolved > 0);
}

export function removeQuestionMarks(newQuestionText: string) {
  return newQuestionText.replace(/\?/g, "");
}

const capitalizeFirst = (text: string) =>
  text?.charAt ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : "";

export function getTranslatedQuestionText(text = "") {
  return `${capitalizeFirst(removeQuestionMarks(text).trim())}?`;
}

//TODO: Fix test
export function canVote(endDate: number): boolean {
  const today = new Date();

  // Create a Date object from the endDate timestamp
  const endDateObj = new Date(endDate);

  // Check if the year, month, and day match
  return (
    today.getFullYear() === endDateObj.getFullYear() &&
    today.getMonth() === endDateObj.getMonth() &&
    today.getDate() === endDateObj.getDate()
  );
}
